import { InputAdornment, TextField } from "@mui/material";
import { CaretDownIcon, UsersIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { cabinClassLabelKeys } from "@/features/flights/utils/cabinClasses";
import {
  FlightSearchTrackingEvents,
  rudderAnalytics,
  type SearchSource,
} from "@/lib/rudderstack";

import { TravellersSelection } from "../components/PassengersSelection";
import type { FlightSearchForm } from "../FlightSearchForm";

type FlightTravelersPickerProps = {
  form: FlightSearchForm;
  analyticsSource: SearchSource;
};

export function FlightTravelersPicker({
  form,
  analyticsSource,
}: FlightTravelersPickerProps) {
  const intl = useIntl();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [adult, child, infantWithoutSeat, cabinClass] = useWatch({
    control: form.control,
    name: ["adult", "child", "infantWithoutSeat", "cabinClass"],
  });

  const travellersCount = adult + child + infantWithoutSeat;

  return (
    <>
      <TextField
        name="passengersNo"
        fullWidth
        id="passengersNo"
        label={intl.formatMessage({
          id: "flights.search.travellersCabinClass",
        })}
        className="w-full p-0"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <UsersIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <CaretDownIcon />
              </InputAdornment>
            ),
          },
        }}
        value={intl.formatMessage(
          { id: "flights.search.travellersSumWithCabinClass" },
          {
            travellersCount,
            cabinClass: intl.formatMessage({
              id: cabinClassLabelKeys[cabinClass],
            }),
          },
        )}
        onClick={() => {
          rudderAnalytics.track(
            FlightSearchTrackingEvents.USER_CLICKED_FLIGHT_TRAVELLERS_INPUT,
            {
              source: analyticsSource,
            },
          );

          setIsDrawerOpen(true);
        }}
      />

      <SwipeableDrawer
        hasFullHeight
        onClose={() => setIsDrawerOpen(false)}
        open={isDrawerOpen}
        disableRestoreFocus
        heightOffset={24}
        ModalProps={{
          keepMounted: false,
        }}
      >
        <DrawerTopBar
          title={intl.formatMessage({
            id: "flights.search.travellersAndCabinClass",
          })}
          onCloseClick={() => setIsDrawerOpen(false)}
        />

        <TravellersSelection
          initialValues={{
            adult,
            child,
            infantWithoutSeat,
            cabinClass,
          }}
          onConfirm={(values) => {
            rudderAnalytics.track(
              FlightSearchTrackingEvents.USER_CHANGED_TRAVELLERS_AND_CLASS,
              {
                travellers: {
                  adults: values.adult,
                  children: values.child,
                  infants: values.infantWithoutSeat,
                },
                class: values.cabinClass,
                source: analyticsSource,
              },
            );

            form.reset({
              ...form.getValues(),
              ...values,
            });
            setIsDrawerOpen(false);
          }}
        />
      </SwipeableDrawer>
    </>
  );
}
